import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Mail, Phone, Calendar, User, ExternalLink, UserPlus, Pencil, ShieldAlert, Car, Church, Save, Home, X, Trash2, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { EnrollmentStatusBadge } from '~/components/enrollment';
import EditFamilyInfoDialog from './EditFamilyInfoDialog';
import AddChildDialog from './AddChildDialog';
import { UpdatePersonMutation } from '../StudentDetail/useUpdatePersonMutation';
import { UpdateFamilyMemberMutation } from './useUpdateFamilyMember';
import { DeleteFamilyMemberMutation } from './useDeleteFamilyMember';
import { useInviteToCreateAccount } from './useInviteToCreateAccount';
import { useRevokeAccountInvite } from './useRevokeAccountInvite';
import { GuardianAccountStatus } from './GuardianAccountStatus';
import { GuardianEmailsToggle } from './GuardianEmailsToggle';
import { HouseholdChangeReviewPanel } from './HouseholdChangeReviewPanel';
import type {
  FamilyGeneralTabProps,
  Guardian,
  Child,
  FamilyAddress,
  FamilyContact,
  ChildPerson,
} from './types';

function formatDate(dateString: string | null | undefined) {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    // ISO8601Date birthdays are calendar dates, parsed at UTC midnight.
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatAddress(address: FamilyAddress | null | undefined) {
  if (!address) return null;
  const parts = [
    address.street,
    address.street2,
    [address.city, address.state, address.zip].filter(Boolean).join(', '),
    address.country,
  ].filter((v): v is string => Boolean(v));
  return parts.length > 0 ? parts : null;
}

function getParentLabel(gender: string | null | undefined) {
  if (gender === 'MALE') return 'Father';
  if (gender === 'FEMALE') return 'Mother';
  return null;
}

interface EditableParentCardProps {
  member: Guardian;
  hasChanges: boolean;
  editedData: Record<string, Record<string, any>>;
  onDataChange: (personId: string, field: string, value: any) => void;
  onSetGender: (personId: string, gender: "MALE" | "FEMALE") => void;
  onInviteAccount?: (personId: string, email?: string, confirmUnknown?: boolean) => void;
  onRevokeAccount?: (personId: string) => void;
  accountBusy?: boolean;
  familyId?: string;
  onEmailsChanged?: () => void;
}

function EditableParentCard({ member, hasChanges, editedData, onDataChange, onSetGender, onInviteAccount, onRevokeAccount, accountBusy, familyId, onEmailsChanged }: EditableParentCardProps) {
  const person = member.person;
  const parentLabel = getParentLabel(person.gender);

  const handleChange = (field: string, value: any) => {
    onDataChange(person.id, field, value);
  };

  return (
    <Card className={hasChanges ? 'ring-2 ring-primary' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {parentLabel ? (
              <span className="text-base font-medium text-muted-foreground">{parentLabel}</span>
            ) : (
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSetGender(person.id, 'MALE')}
                  className="text-xs h-7 px-2"
                >
                  Set as Father
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSetGender(person.id, 'FEMALE')}
                  className="text-xs h-7 px-2"
                >
                  Set as Mother
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Checkbox
                id={`primary-${person.id}`}
                checked={editedData[person.id]?.isPrimary ?? member.isPrimary}
                onCheckedChange={(checked: boolean) => handleChange('isPrimary', checked)}
              />
              <Label htmlFor={`primary-${person.id}`} className="text-xs text-muted-foreground">
                Primary
              </Label>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor={`firstName-${person.id}`} className="text-xs">First Name</Label>
            <Input
              id={`firstName-${person.id}`}
              value={editedData[person.id]?.firstName ?? person.firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('firstName', e.target.value)}
              className="h-8"
            />
          </div>
          <div>
            <Label htmlFor={`lastName-${person.id}`} className="text-xs">Last Name</Label>
            <Input
              id={`lastName-${person.id}`}
              value={editedData[person.id]?.lastName ?? person.lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('lastName', e.target.value)}
              className="h-8"
            />
          </div>
        </div>
        <div>
          <Label htmlFor={`email-${person.id}`} className="text-xs flex items-center gap-1">
            <Mail className="h-3 w-3" />
            Email
          </Label>
          <Input
            id={`email-${person.id}`}
            type="email"
            value={editedData[person.id]?.email ?? person.email ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)}
            className="h-8"
          />
        </div>
        {onInviteAccount && onRevokeAccount && (
          <GuardianAccountStatus
            personId={person.id}
            email={person.email}
            hasUserAccount={person.hasUserAccount ?? false}
            accountInviteStatus={person.accountInviteStatus ?? null}
            accountInviteNoticeStatus={person.accountInviteNoticeStatus}
            accountInviteNoticeReason={person.accountInviteNoticeReason}
            accountInviteNoticeAt={person.accountInviteNoticeAt as string | null}
            accountInviteNoticeOutcomeUnknown={person.accountInviteNoticeOutcomeUnknown}
            accountInviteEmail={person.accountInviteEmail ?? null}
            accountInviteExpiresAt={(person.accountInviteExpiresAt as string | null) ?? null}
            onInvite={onInviteAccount}
            onRevoke={onRevokeAccount}
            busy={accountBusy}
          />
        )}
        {familyId && (
          <GuardianEmailsToggle
            familyId={familyId}
            personId={person.id}
            receivesEmails={member.receivesEmails}
            onChanged={onEmailsChanged}
          />
        )}
        <div>
          <Label htmlFor={`phone-${person.id}`} className="text-xs flex items-center gap-1">
            <Phone className="h-3 w-3" />
            Phone
          </Label>
          <Input
            id={`phone-${person.id}`}
            type="tel"
            value={editedData[person.id]?.phone ?? person.phone ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('phone', e.target.value)}
            className="h-8"
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface ChildCardProps {
  member: Child;
  schoolSlug: string;
  selectedYear: { id: string; name: string } | null;
  onAddToYear: ((person: ChildPerson) => void) | undefined;
  onDelete: ((member: Child) => void) | undefined;
}

function ChildCard({ member, schoolSlug, selectedYear, onAddToYear, onDelete }: ChildCardProps) {
  const person = member.person;
  const fullName = `${person.firstName} ${person.lastName}`;
  const enrollment = person.studentEnrollmentForYear;
  const hasEnrollment = !!enrollment;

  // Use studentProfile dateOfBirth if available, otherwise fall back to person.dateOfBirth
  const dateOfBirth = (person.studentProfile?.dateOfBirth || person.dateOfBirth) as string | null;

  // Can delete if no completed enrollments
  const state = enrollment?.state as string | undefined;
  const hasCompletedEnrollment = state === 'COMPLETED' || state === 'PACKET_COMPLETED';
  const canDelete = !hasCompletedEnrollment;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2">
          <Link
            to={`/app/${schoolSlug}/students/${person.id}`}
            className="text-lg hover:underline flex items-center gap-1"
          >
            {fullName}
            <ExternalLink className="h-3 w-3" />
          </Link>
          <div className="flex items-center gap-2">
            {hasEnrollment && (
              <EnrollmentStatusBadge
                state={enrollment.state}
                type="student"
              />
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={() => onDelete?.(member)}
                aria-label={`Remove ${fullName} from family`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          {dateOfBirth && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(dateOfBirth)}</span>
            </div>
          )}
          {person.gender && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              <span>{person.gender === 'MALE' ? 'Male' : 'Female'}</span>
            </div>
          )}
          {!dateOfBirth && !person.gender && (
            <p className="text-sm text-muted-foreground">No additional information</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            asChild
          >
            <Link to={`/app/${schoolSlug}/students/${person.id}`}>
              View Student
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/app/${schoolSlug}/students/${person.id}/grades`}>
              Grades
            </Link>
          </Button>
          {selectedYear && !hasEnrollment && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onAddToYear?.(person)}
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Add to {selectedYear.name}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface ContactsSectionProps {
  contacts: FamilyContact[] | null | undefined;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
}

function ContactsSection({ contacts, title, icon: Icon }: ContactsSectionProps) {
  if (!contacts || contacts.length === 0) {
    return (
      <p className="text-muted-foreground italic">No {title.toLowerCase()} added.</p>
    );
  }

  return (
    <div className="space-y-2">
      {contacts.map((contact, index) => (
        <div key={index} className="flex items-start gap-3 p-2 rounded-md bg-muted/50">
          <Icon className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">{contact.name}</p>
            <div className="flex flex-wrap gap-x-4 text-sm text-muted-foreground">
              {contact.phone && <span>{contact.phone}</span>}
              {contact.relationship && <span>{contact.relationship}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface AddressCardProps {
  address: FamilyAddress | null | undefined;
  onEdit: () => void;
}

function AddressCard({ address, onEdit }: AddressCardProps) {
  const formattedAddress = formatAddress(address);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            Home Address
          </span>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {formattedAddress ? (
          <div className="space-y-0.5 text-sm">
            {formattedAddress.map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground italic">No address on file.</p>
        )}
      </CardContent>
    </Card>
  );
}

export function FamilyGeneralTab({ family, schoolSlug, checklistEnrollmentOn = false, selectedYear, onAddChildToYear, onRefetch }: FamilyGeneralTabProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddChildDialogOpen, setIsAddChildDialogOpen] = useState(false);
  const [editedGuardianData, setEditedGuardianData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [accountInviteError, setAccountInviteError] = useState<string | null>(null);
  const [deleteChildDialog, setDeleteChildDialog] = useState<{ open: boolean; member: Child | null }>({ open: false, member: null });
  const [deleteChildError, setDeleteChildError] = useState<string | null>(null);

  const [updatePerson] = useMutation(UpdatePersonMutation);
  const [updateFamilyMember] = useMutation(UpdateFamilyMemberMutation);
  const [deleteFamilyMember, { loading: deletingChild }] = useMutation(DeleteFamilyMemberMutation, {
    onCompleted: (result) => {
      if (result.deleteFamilyMember?.success) {
        onRefetch?.();
        setDeleteChildDialog({ open: false, member: null });
        setDeleteChildError(null);
        return;
      }

      setDeleteChildError(
        result.deleteFamilyMember?.userErrors?.map((item) => item.message).join(', ') ||
          'Unable to remove this child.',
      );
    },
    onError: (error) => setDeleteChildError(error.message || 'Unable to remove this child.'),
  });
  const [inviteToCreateAccount, { loading: inviting }] = useInviteToCreateAccount();
  const [revokeAccountInvite, { loading: revoking }] = useRevokeAccountInvite();

  const guardians = useMemo(() => family?.guardians || [], [family?.guardians]);
  const children = family?.children || [];
  const emergencyContacts = family?.emergencyContacts || [];
  const authorizedPickup = family?.authorizedPickup || [];
  const churchAffiliation = family?.churchAffiliation;
  const address = family?.address;

  // Check if there are any pending changes
  const hasChanges = useMemo(() => {
    return Object.keys(editedGuardianData).length > 0;
  }, [editedGuardianData]);

  // Check which guardians have changes
  const guardianHasChanges = useCallback((personId: string) => {
    return editedGuardianData[personId] !== undefined;
  }, [editedGuardianData]);

  // Handle data change for a guardian
  const handleGuardianDataChange = useCallback((personId: string, field: string, value: any) => {
    setEditedGuardianData(prev => {
      const currentData = prev[personId] || {};
      const guardian = guardians.find((g: Guardian) => g.person.id === personId);
      if (!guardian) return prev;
      const originalValue = field === 'isPrimary'
        ? guardian.isPrimary
        : (guardian.person as Record<string, any>)[field];

      // If the value is the same as original, potentially remove the field
      if (value === originalValue) {
        const { [field]: _, ...rest } = currentData;
        if (Object.keys(rest).length === 0) {
          const { [personId]: __, ...restData } = prev;
          return restData;
        }
        return { ...prev, [personId]: rest };
      }

      return {
        ...prev,
        [personId]: { ...currentData, [field]: value }
      };
    });
  }, [guardians]);

  // Handle setting gender (immediate save)
  const handleSetGender = useCallback(async (personId: string, gender: "MALE" | "FEMALE") => {
    try {
      await updatePerson({
        variables: {
          input: {
            personId,
            gender
          }
        }
      });
      onRefetch?.();
    } catch (error) {
      console.error('Failed to update gender:', error);
    }
  }, [updatePerson, onRefetch]);

  const handleInviteAccount = useCallback(async (personId: string, email?: string, confirmUnknown?: boolean) => {
    setAccountInviteError(null);

    try {
      const result = await inviteToCreateAccount({ variables: { personId, email: email ?? null, ...(confirmUnknown ? { confirmUnknown: true } : {}) } });
      const payload = result.data?.inviteToCreateAccount;
      const userErrors = payload?.userErrors;

      if (userErrors?.length) {
        setAccountInviteError(userErrors.map((error) => error.message).join(', '));
        return;
      }

      if (!payload) {
        setAccountInviteError('Unable to send the account invitation. Please try again.');
        return;
      }

      onRefetch?.();
    } catch {
      setAccountInviteError('Unable to send the account invitation. Please try again.');
    }
  }, [inviteToCreateAccount, onRefetch]);

  const handleRevokeAccount = useCallback(async (personId: string) => {
    await revokeAccountInvite({ variables: { personId } });
    onRefetch?.();
  }, [revokeAccountInvite, onRefetch]);

  // Save all changes
  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      const promises: Promise<any>[] = [];

      for (const [personId, changes] of Object.entries(editedGuardianData)) {
        const { isPrimary, ...personChanges } = changes;

        // Update person if there are person field changes
        if (Object.keys(personChanges).length > 0) {
          promises.push(
            updatePerson({
              variables: {
                input: {
                  personId,
                  ...personChanges
                }
              }
            })
          );
        }

        // Update family member if isPrimary changed
        if (isPrimary !== undefined) {
          promises.push(
            updateFamilyMember({
              variables: {
                input: {
                  familyId: family.id,
                  personId,
                  isPrimary
                }
              }
            })
          );
        }
      }

      await Promise.all(promises);
      setEditedGuardianData({});
      onRefetch?.();
    } catch (error) {
      console.error('Failed to save changes:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editedGuardianData, family?.id, updatePerson, updateFamilyMember, onRefetch]);

  // Cancel changes
  const handleCancelChanges = useCallback(() => {
    setEditedGuardianData({});
  }, []);

  return (
    <div className="space-y-8">
      {/* Parents Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Parents</h2>
          {hasChanges && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelChanges}
                disabled={isSaving}
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveChanges}
                disabled={isSaving}
                className="bg-primary"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
        {accountInviteError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {accountInviteError}
          </div>
        )}
        {guardians.length === 0 ? (
          <p className="text-muted-foreground">No parents on record.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {guardians.map((guardian: Guardian, index: number) => (
              <EditableParentCard
                key={guardian.person.id || index}
                member={guardian}
                hasChanges={guardianHasChanges(guardian.person.id)}
                editedData={editedGuardianData}
                onDataChange={handleGuardianDataChange}
                onSetGender={handleSetGender}
                onInviteAccount={handleInviteAccount}
                onRevokeAccount={handleRevokeAccount}
                accountBusy={inviting || revoking}
                familyId={checklistEnrollmentOn ? family?.id : undefined}
                onEmailsChanged={onRefetch}
              />
            ))}
          </div>
        )}
      </section>

      {/* Children Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Children</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAddChildDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Child
          </Button>
        </div>
        {children.length === 0 ? (
          <p className="text-muted-foreground">No children on record.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {children.map((child: Child, index: number) => (
              <ChildCard
                key={child.person.id || index}
                member={child}
                schoolSlug={schoolSlug}
                selectedYear={selectedYear}
                onAddToYear={onAddChildToYear}
                onDelete={(member) => setDeleteChildDialog({ open: true, member })}
              />
            ))}
          </div>
        )}
      </section>

      {/* Delete Child Confirmation Dialog */}
      <AlertDialog
        open={deleteChildDialog.open}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setDeleteChildDialog({ open: false, member: null });
            setDeleteChildError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Child from Family</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <strong>{deleteChildDialog.member?.person?.firstName} {deleteChildDialog.member?.person?.lastName}</strong>{' '}
              from this family? This will also remove any pending enrollments for this student. This action cannot be undone.
            </AlertDialogDescription>
            {deleteChildError && (
              <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {deleteChildError}
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                setDeleteChildError(null);
                void deleteFamilyMember({
                  variables: {
                    familyId: family.id,
                    personId: deleteChildDialog.member!.person.id,
                    deletePerson: true,
                  },
                });
              }}
              disabled={deletingChild}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingChild ? 'Removing...' : 'Remove Child'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* A family's own request to change this record, waiting on a decision. It sits
          directly above the record it would change, so the two read together. */}
      {family?.pendingHouseholdChangeRequest && (
        <HouseholdChangeReviewPanel
          request={family.pendingHouseholdChangeRequest}
          onReviewed={onRefetch}
        />
      )}

      {/* Address & Household Info Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Household Information</h2>
          <Button variant="outline" size="sm" onClick={() => setIsEditDialogOpen(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Address */}
          <AddressCard address={address} onEdit={() => setIsEditDialogOpen(true)} />

          {/* Emergency Contacts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                Emergency Contacts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ContactsSection
                contacts={emergencyContacts}
                title="Emergency Contacts"
                icon={Phone}
              />
            </CardContent>
          </Card>

          {/* Authorized Pickup */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Car className="h-4 w-4" />
                Authorized Pickup
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ContactsSection
                contacts={authorizedPickup}
                title="Authorized Pickup"
                icon={User}
              />
            </CardContent>
          </Card>

          {/* Church Affiliation */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Church className="h-4 w-4" />
                Church Affiliation
              </CardTitle>
            </CardHeader>
            <CardContent>
              {churchAffiliation?.churchName || churchAffiliation?.pastorName ? (
                <div className="space-y-1">
                  {churchAffiliation.churchName && (
                    <p className="font-medium">{churchAffiliation.churchName}</p>
                  )}
                  {churchAffiliation.pastorName && (
                    <p className="text-sm text-muted-foreground">
                      Pastor: {churchAffiliation.pastorName}
                    </p>
                  )}
                  {churchAffiliation.churchEmail && (
                    <p className="text-sm text-muted-foreground">
                      {churchAffiliation.churchEmail}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No church affiliation added.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Edit Dialog */}
      <EditFamilyInfoDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        family={family}
        onSuccess={onRefetch}
      />

      {/* Add Child Dialog */}
      <AddChildDialog
        open={isAddChildDialogOpen}
        onOpenChange={setIsAddChildDialogOpen}
        familyId={family?.id}
        familyName={family?.name}
        onSuccess={onRefetch}
      />
    </div>
  );
}

export default FamilyGeneralTab;
